// Goods in: the Teams cards the goods-in chat missed (rule 26, migration 023).
//
// A card that ran out of attempts (GAVE_UP) or was too old to post (STALE) never reached the chat.
// The chat cannot say so; this panel does (rule 15). When the list could not be read it says that,
// never "nothing missed"; when notices are off it says the chat is not fed.
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { officeFocus, useResource } from '../../shells/OfficeShell';
import { stamp } from './goodsInModel';

export interface MissedNotice {
  noticeId: number;
  kind: string;
  outcome: 'STALE' | 'GAVE_UP';
  reason: string | null;
  createdAt: string | null;
  endedAt: string | null;
  receiptId: number | null;
}

export interface MissedPayload {
  mode: 'off' | 'on' | 'not_configured';
  windowDays: number;
  missed: MissedNotice[];
  summary: string;
  /** Absent from an older API. notReached null = the record could not be read (rule 15). */
  urgent?: {
    mode: 'off' | 'on' | 'not_configured';
    notReached: Array<{ upn: string; name: string; reason: 'not_installed' | 'no_id' | 'failed' | 'refused'; at: string }> | null;
    alert?: { on: boolean; askedToday: number | null; cap: number };
  };
}

const KIND: Record<string, string> = {
  RECEIVED: 'Received, inspect now',
  INSPECTION: 'Inspection result',
  FAILURE_CLOSED: 'Failure closed',
  COFA_RECORDED: 'CofA recorded',
  COFA_DECISION: 'CofA decision',
  REMINDER: 'Reminder',
  ALERT_HEALTH: 'Could not check D365',
  BATCH_HOLD: 'QC Hold',
};

/** What a missed card's row says: the card, and why it never reached the chat. */
export function missedLine(m: MissedNotice): string {
  const why = m.outcome === 'STALE' ? 'too old to post' : 'gave up';
  return `${KIND[m.kind] ?? m.kind} card #${m.noticeId}: ${why}${m.reason ? ` - ${m.reason}` : ''}`;
}

const REACH: Record<'not_installed' | 'no_id' | 'failed' | 'refused', string> = {
  not_installed: 'Quality Bot is not installed for them',
  no_id: 'their Teams account could not be read',
  failed: 'the bot could not send it',
  refused: 'the Urgent flow refused it or the day limit was reached',
};

/** The urgent 1:1, in a sentence: nothing when it is off or reaching everyone (rule 15: a failed read says so). */
export function urgentLines(u: MissedPayload['urgent']): string[] {
  if (!u) return [];
  const lines: string[] = [];
  if (u.alert?.on) {
    lines.push(
      u.alert.askedToday === null
        ? 'Urgent alerts are on, but today\'s count could not be read: until it can, people get an ordinary ping instead.'
        : `Urgent alerts: asked Teams for ${u.alert.askedToday} of ${u.alert.cap} today. Teams does not tell the app they arrived.`,
    );
  }
  if (u.mode === 'not_configured') lines.push('Urgent 1:1 cards are on but the Quality bot is not configured: nobody is getting them.');
  if (u.notReached === null) lines.push('Could not read who the urgent 1:1 cards reached. Not known is not "everyone".');
  else lines.push(...u.notReached.map((p) => `Urgent 1:1 did not reach ${p.name}: ${REACH[p.reason]}.`));
  return lines;
}

/** Tells the tab its count: a number once read, null when it could not be read (rule 15: never 0 for unknown). */
export function GoodsInNotices({ onCount }: { onCount?: (n: number | null) => void } = {}) {
  const res = useResource<MissedPayload>('/goods-in-notices');
  // The tab's count: cards the chat missed. Off = no count (there is no chat to miss).
  useEffect(
    () => onCount?.(res.error ? null : !res.data || res.data.mode === 'off' ? null : res.data.missed.length),
    [res.error, res.data, onCount],
  );
  if (res.error) {
    return (
      <section aria-label="Teams notices" className="flex flex-wrap items-center gap-(--s2)">
        <p role="alert" className="m-0 font-semibold text-(--warn)">
          Could not read whether the goods-in Teams chat missed any card: {res.error} Not known is not "none missed".
        </p>
        <Button variant="outline" type="button" onClick={res.reload}>
          Try again
        </Button>
      </section>
    );
  }
  // (UI/UX audit, 31 Oct 2026, UX-10, rule 15) Still loading is not "nothing to show": the tab's
  // empty-panel sentence used to stand in for both. Off stays null (that sentence is then true).
  if (!res.data) return <p className="m-0 text-(length:--fs-sm) text-(--muted)" role="status">Loading…</p>;
  if (res.data.mode === 'off') return null;
  const { missed, summary } = res.data;
  const urgent = urgentLines(res.data.urgent);
  return (
    <section aria-label="Teams notices" className="flex flex-col gap-(--s2)">
      {/* (UI/UX audit, 31 Oct 2026, UX-48) "Teams cards", not "Teams chat": this panel is only the
          check for cards that never reached the chat, not the chat itself. The chat's address is not
          in this payload, so no link is offered rather than one guessed. */}
      <h2 className="m-0 text-(length:--fs-md) font-semibold">Teams cards</h2>
      <p className="m-0 text-(length:--fs-sm) text-(--muted)">Cards the goods-in Teams chat should have had and did not: read the rest in the chat itself.</p>
      <p className={`m-0 text-(length:--fs-sm) ${missed.length > 0 || res.data.mode !== 'on' ? 'font-semibold text-(--warn)' : 'text-(--muted)'}`}>{summary}</p>
      {missed.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-(--s1) p-0 text-(length:--fs-sm)">
          {missed.map((m) => (
            <li key={m.noticeId}>
              {missedLine(m)} <span className="text-(--muted)">({stamp(m.endedAt)})</span>{' '}
              {m.receiptId ? (
                <Link to={`/office/goods-in/${m.receiptId}`} className={`underline ${officeFocus}`}>
                  Open the PO line
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {urgent.map((line) => (
        <p key={line} className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          {line}
        </p>
      ))}
    </section>
  );
}
